import { DASHBOARD_STATES } from '../constants';
import { getDuplicateBaseTitle } from '../utils/dashboardDisplayTitle';
import { flattenSectionsForFilter } from '../utils/dashboardFormatters';

const sections = [
  {
    id: 'home',
    name: 'Home',
    supportsDashboard: false,
    children: [{ id: 'home-overview', name: 'Overview', supportsDashboard: true, children: [] }],
  },
  {
    id: 'contacts',
    name: 'Contacts',
    supportsDashboard: false,
    children: [
      {
        id: 'contacts-ucp',
        name: 'UCP',
        supportsDashboard: false,
        children: [
          { id: 'ucp-overview', name: 'Overview', supportsDashboard: true, children: [] },
          { id: 'ucp-intelligence', name: 'Intelligence', supportsDashboard: true, children: [] },
        ],
      },
    ],
  },
  {
    id: 'workspace',
    name: 'Workspace',
    supportsDashboard: true,
    children: [{ id: 'workspace-overview', name: 'Overview', supportsDashboard: true, children: [] }],
  },
];

const templates = [
  {
    id: 'template-ucp-overview',
    titleKey: 'seed.templates.ucpOverview.title',
    descriptionKey: 'seed.templates.ucpOverview.description',
    title: 'UCP Overview',
    description:
      'Provide a detailed illustration of the performance metrics for a client, including key indicators such as revenue and churn rates.',
    widgetCount: 8,
    previewImage: null,
    tags: ['UCP', 'Sales', 'Risk', 'Service', 'Churn'],
    sectionPath: 'Contacts / UCP',
    sectionId: 'contacts-ucp',
    categoryId: 'performance',
    state: DASHBOARD_STATES.PUBLISHED,
    creatorName: 'AIMS Team',
    updatedAt: '2026-03-11T17:00:00Z',
  },
  {
    id: 'template-ucp-intelligence',
    titleKey: 'seed.templates.ucpIntelligence.title',
    descriptionKey: 'seed.templates.ucpIntelligence.description',
    title: 'UCP Intelligence',
    description:
      'Discover valuable insights from your clients through advanced AI technology. This data offers a deeper understanding of client behavior.',
    widgetCount: 8,
    previewImage: null,
    tags: ['UCP', 'Sales', 'Risk', 'Service', 'Churn'],
    sectionPath: 'Contacts / UCP',
    sectionId: 'contacts-ucp',
    categoryId: 'performance',
    state: DASHBOARD_STATES.PUBLISHED,
    creatorName: 'AIMS Team',
    updatedAt: '2026-03-10T12:00:00Z',
  },
  {
    id: 'template-home-dashboard',
    titleKey: 'seed.templates.homeDashboard.title',
    descriptionKey: 'seed.templates.homeDashboard.description',
    title: 'Home Dashboard',
    description:
      'Find key information summarizing the most relevant updates. These insights will help you make informed decisions by highlighting trends.',
    widgetCount: 4,
    previewImage: null,
    tags: ['UCP', 'Sales', 'Risk', 'Service', 'Churn'],
    sectionPath: 'Home',
    sectionId: 'home',
    categoryId: 'operations',
    state: DASHBOARD_STATES.PUBLISHED,
    creatorName: 'AIMS Team',
    updatedAt: '2026-03-09T12:00:00Z',
  },
  {
    id: 'template-sales-performance',
    titleKey: 'seed.templates.salesPerformance.title',
    descriptionKey: 'seed.templates.salesPerformance.description',
    title: 'Sales Performance',
    description: 'Pre-configured dashboard for sales teams with pipeline widgets.',
    widgetCount: 4,
    previewImage: null,
    tags: ['Sales', 'Performance'],
    sectionId: 'workspace',
    categoryId: 'sales',
    state: DASHBOARD_STATES.PUBLISHED,
    creatorName: 'AIMS Team',
    updatedAt: '2026-03-08T12:00:00Z',
  },
];

const templateCategories = [
  { id: 'sales', name: 'Sales' },
  { id: 'performance', name: 'Performance' },
  { id: 'operations', name: 'Operations' },
  { id: 'support', name: 'Support' },
];

const SEED_DASHBOARDS = [
  {
    id: 'dash-001',
    titleKey: 'seed.dashboards.salesOverview.title',
    descriptionKey: 'seed.dashboards.salesOverview.description',
    title: 'Sales Overview Dashboard',
    sectionPath: 'Contacts / UCP / Overview',
    sectionId: 'ucp-overview',
    categoryId: 'sales',
    state: DASHBOARD_STATES.PUBLISHED,
    description:
      'Key metrics for the sales team including pipeline and conversion rates across all regions.',
    creatorName: 'David Kim',
    createdAt: '2026-02-15T10:30:00Z',
    updatedAt: '2026-03-10T14:22:00Z',
    widgetCount: 2,
    tags: ['Sales', 'Pipeline', 'KPI', 'Monthly', 'Forecast', 'Revenue', 'Quarterly'],
  },
  {
    id: 'dash-002',
    titleKey: 'seed.dashboards.contactIntelligence.title',
    descriptionKey: 'seed.dashboards.contactIntelligence.description',
    title: 'Contact Intelligence',
    sectionPath: 'Contacts / UCP / Intelligence',
    sectionId: 'ucp-intelligence',
    categoryId: 'performance',
    state: DASHBOARD_STATES.DRAFT,
    description:
      'AI-powered insights and recommendations for contact engagement and outreach strategy.',
    creatorName: 'Maria Garcia',
    createdAt: '2026-03-01T09:00:00Z',
    updatedAt: '2026-03-11T16:45:00Z',
    widgetCount: 1,
    tags: ['Intelligence', 'AI', 'Recommendations'],
  },
  {
    id: 'dash-003',
    titleKey: 'seed.dashboards.marketingCampaignTracker.title',
    descriptionKey: 'seed.dashboards.marketingCampaignTracker.description',
    title: 'Marketing Campaign Tracker',
    sectionPath: 'Contacts / UCP / Overview',
    sectionId: 'ucp-overview',
    categoryId: 'sales',
    state: DASHBOARD_STATES.PUBLISHED,
    description: 'Monitor campaign performance, ad spend, and ROI across all channels.',
    creatorName: 'Carlos Ruiz',
    createdAt: '2026-02-20T08:15:00Z',
    updatedAt: '2026-03-09T11:30:00Z',
    widgetCount: 4,
    tags: ['Marketing', 'Campaigns', 'Analytics'],
  },
  {
    id: 'dash-004',
    titleKey: 'seed.dashboards.customerSupportOverview.title',
    descriptionKey: 'seed.dashboards.customerSupportOverview.description',
    title: 'Customer Support Overview',
    sectionPath: 'Contacts / UCP / Overview',
    sectionId: 'ucp-overview',
    categoryId: 'support',
    state: DASHBOARD_STATES.DRAFT,
    description: 'Track support tickets, response times, and customer satisfaction scores.',
    creatorName: 'David Kim',
    createdAt: '2026-03-05T14:00:00Z',
    updatedAt: '2026-03-12T09:20:00Z',
    widgetCount: 3,
    tags: ['Support', 'Tickets', 'CSAT'],
  },
  {
    id: 'dash-005',
    titleKey: 'seed.dashboards.executiveSummary.title',
    descriptionKey: 'seed.dashboards.executiveSummary.description',
    title: 'Executive Summary',
    sectionPath: 'Contacts / UCP / Intelligence',
    sectionId: 'ucp-intelligence',
    categoryId: 'performance',
    state: DASHBOARD_STATES.PUBLISHED,
    description: 'High-level KPIs and financial metrics for leadership.',
    creatorName: 'Maria Garcia',
    createdAt: '2026-01-10T09:00:00Z',
    updatedAt: '2026-03-11T17:00:00Z',
    widgetCount: 6,
    tags: ['Executive', 'KPI', 'Finance', 'Summary'],
  },
  {
    id: 'dash-006',
    titleKey: 'seed.dashboards.productAnalytics.title',
    descriptionKey: 'seed.dashboards.productAnalytics.description',
    title: 'Product Analytics',
    sectionPath: 'Contacts / UCP / Overview',
    sectionId: 'ucp-overview',
    categoryId: 'performance',
    state: DASHBOARD_STATES.DRAFT,
    description: 'User engagement, feature adoption, and product usage metrics.',
    creatorName: 'Carlos Ruiz',
    createdAt: '2026-03-08T10:00:00Z',
    updatedAt: '2026-03-12T08:15:00Z',
    widgetCount: 5,
    tags: ['Product', 'Analytics', 'Engagement'],
  },
  {
    id: 'dash-007',
    titleKey: 'seed.dashboards.hrOnboarding.title',
    descriptionKey: 'seed.dashboards.hrOnboarding.description',
    title: 'HR & Onboarding',
    sectionPath: 'Contacts / UCP / Intelligence',
    sectionId: 'ucp-intelligence',
    categoryId: 'operations',
    state: DASHBOARD_STATES.ARCHIVED,
    description: 'New hire progress, training completion, and team headcount.',
    creatorName: 'Ana Lopez',
    createdAt: '2026-02-28T16:30:00Z',
    updatedAt: '2026-03-10T12:00:00Z',
    widgetCount: 2,
    tags: ['HR', 'Onboarding', 'People'],
  },
];

/**
 * Widget catalog aligned with legacy ann-web SEED_WIDGET_LIBRARY.
 * `libraryIcon`: Lucide name from widgetLibraryIconMap, or null for empty avatar ring (legacy UI parity).
 */
const widgetLibrary = [
  {
    id: 'widget-notes',
    name: 'Notes',
    type: 'notes',
    category: 'Communication',
    description: 'Display and manage notes for the current context.',
    bestFor:
      'Keeping track of important observations, follow-ups, and internal notes about contacts',
    libraryIcon: null,
    widgetClass: 'CLASS_2_STANDARD',
    defaultActions: ['add'],
    sizes: ['small', 'medium', 'large'],
    defaultSize: 'medium',
  },
  {
    id: 'widget-activity',
    name: 'Activity Feed',
    type: 'activity',
    category: 'Communication',
    description: 'View recent calls, emails, and interactions.',
    bestFor: 'Tracking recent interactions and events, monitoring team activity in real-time',
    libraryIcon: null,
    widgetClass: 'CLASS_2_STANDARD',
    defaultActions: [],
    sizes: ['small', 'medium', 'large'],
    defaultSize: 'large',
  },
  {
    id: 'widget-emails',
    name: 'Emails',
    type: 'emails',
    category: 'Communication',
    description: 'View and manage email conversations linked to the current contact.',
    bestFor: 'Centralizing email threads and keeping communication history accessible',
    libraryIcon: null,
    widgetClass: 'CLASS_2_STANDARD',
    defaultActions: [],
    sizes: ['small', 'medium', 'large'],
    defaultSize: 'medium',
  },
  {
    id: 'widget-chat',
    name: 'Chat Messages',
    type: 'chat',
    category: 'Communication',
    description: 'Real-time chat messages and conversation threads.',
    bestFor: 'Monitoring live conversations and quick internal messaging',
    libraryIcon: null,
    widgetClass: 'CLASS_2_STANDARD',
    defaultActions: [],
    sizes: ['small', 'medium', 'large'],
    defaultSize: 'medium',
  },
  {
    id: 'widget-appointments',
    name: 'Appointments',
    type: 'appointments',
    category: 'Scheduling',
    description: 'Upcoming and past appointments for the current context.',
    bestFor: 'Scheduling oversight and ensuring no meetings are missed',
    libraryIcon: 'Calendar',
    widgetClass: 'CLASS_1_COMPACT',
    defaultActions: [],
    sizes: ['small', 'medium'],
    defaultSize: 'small',
  },
  {
    id: 'widget-tasks',
    name: 'Tasks',
    type: 'tasks',
    category: 'Scheduling',
    description: 'Track pending, in-progress, and completed tasks.',
    bestFor: 'Organizing daily work, assigning responsibilities, and tracking completion',
    libraryIcon: 'CheckCircle2',
    widgetClass: 'CLASS_2_STANDARD',
    defaultActions: ['add'],
    sizes: ['small', 'medium', 'large'],
    defaultSize: 'medium',
  },
  {
    id: 'widget-follow-ups',
    name: 'Follow-ups',
    type: 'followUps',
    category: 'Scheduling',
    description: 'Scheduled follow-ups and reminders for contacts.',
    bestFor: 'Never missing a follow-up and staying on top of outreach cadences',
    libraryIcon: 'Clock',
    widgetClass: 'CLASS_1_COMPACT',
    defaultActions: ['add'],
    sizes: ['small', 'medium'],
    defaultSize: 'small',
  },
  {
    id: 'widget-pipeline',
    name: 'Pipeline Overview',
    type: 'pipeline',
    category: 'Analytics',
    description: 'Visualize deal stages, values, and progression through the sales pipeline.',
    bestFor: 'Understanding deal flow, identifying bottlenecks, and forecasting revenue',
    libraryIcon: null,
    widgetClass: 'CLASS_3_FULL',
    defaultActions: [],
    sizes: ['small', 'medium', 'large'],
    defaultSize: 'large',
  },
  {
    id: 'widget-conversion-funnel',
    name: 'Conversion Funnel',
    type: 'conversionFunnel',
    category: 'Analytics',
    description: 'Track conversion rates across each stage of the customer journey.',
    bestFor: 'Identifying drop-off points and optimizing the lead-to-customer path',
    libraryIcon: null,
    widgetClass: 'CLASS_2_STANDARD',
    defaultActions: [],
    sizes: ['small', 'medium', 'large'],
    defaultSize: 'medium',
  },
  {
    id: 'widget-performance-score',
    name: 'Performance Score',
    type: 'performanceScore',
    category: 'Analytics',
    description: 'Aggregated performance metrics with trend indicators and benchmarks.',
    bestFor: 'Quick health-check on KPIs and comparing against team or historical targets',
    libraryIcon: null,
    widgetClass: 'CLASS_1_COMPACT',
    defaultActions: [],
    sizes: ['small', 'medium'],
    defaultSize: 'small',
  },
  {
    id: 'widget-ai-insights',
    name: 'AI Insights',
    type: 'aiInsights',
    category: 'Intelligence',
    description: 'AI-generated recommendations and actionable insights for contacts.',
    bestFor:
      'Surfacing hidden opportunities, risk signals, and next-best-action suggestions',
    libraryIcon: 'Sparkles',
    widgetClass: 'CLASS_2_STANDARD',
    defaultActions: [],
    sizes: ['small', 'medium', 'large'],
    defaultSize: 'medium',
  },
  {
    id: 'widget-sentiment',
    name: 'Sentiment Analysis',
    type: 'sentiment',
    category: 'Intelligence',
    description: 'Analyze communication tone and customer sentiment over time.',
    bestFor:
      'Detecting shifts in customer satisfaction and proactively addressing concerns',
    libraryIcon: 'TrendingUp',
    widgetClass: 'CLASS_2_STANDARD',
    defaultActions: [],
    sizes: ['small', 'medium', 'large'],
    defaultSize: 'medium',
  },
];

const shareCandidates = [
  { type: 'user', id: 'usr-001', name: 'David Kim', role: 'Sales Manager' },
  { type: 'user', id: 'usr-002', name: 'Maria Garcia', role: 'Account Executive' },
  { type: 'user', id: 'usr-003', name: 'Carlos Ruiz', role: 'Marketing Lead' },
  { type: 'user', id: 'usr-004', name: 'Ana Lopez', role: 'People Ops' },
  { type: 'department', id: 'dept-001', name: 'Sales Team', membersCount: 12 },
  { type: 'department', id: 'dept-002', name: 'Marketing', membersCount: 8 },
  { type: 'department', id: 'dept-003', name: 'Support', membersCount: 16 },
];

let dashboardsStore = JSON.parse(JSON.stringify(SEED_DASHBOARDS));

const clone = (value) => JSON.parse(JSON.stringify(value));
const wait = (ms = 250) => new Promise((resolve) => setTimeout(resolve, ms));

const applyFilters = (items, filters = {}) => {
  const search = (filters.search || '').trim().toLowerCase();

  const filtered = items.filter((item) => {
    const matchesSearch =
      !search ||
      item.title?.toLowerCase().includes(search) ||
      item.description?.toLowerCase().includes(search);

    const matchesState = !filters.state || item.state === filters.state;
    const matchesSection = !filters.sectionId || item.sectionId === filters.sectionId;
    const matchesCategory = !filters.categoryId || item.categoryId === filters.categoryId;

    return matchesSearch && matchesState && matchesSection && matchesCategory;
  });

  return [...filtered].sort((a, b) => {
    const isAArchived = a?.state === DASHBOARD_STATES.ARCHIVED;
    const isBArchived = b?.state === DASHBOARD_STATES.ARCHIVED;

    if (isAArchived !== isBArchived) return isAArchived ? 1 : -1;

    if (isAArchived && isBArchived) {
      const aArchivedAt = Date.parse(a?.archivedAt || a?.updatedAt || a?.createdAt) || 0;
      const bArchivedAt = Date.parse(b?.archivedAt || b?.updatedAt || b?.createdAt) || 0;
      return aArchivedAt - bArchivedAt;
    }

    const aCreatedAt = Date.parse(a?.createdAt) || 0;
    const bCreatedAt = Date.parse(b?.createdAt) || 0;
    return bCreatedAt - aCreatedAt;
  });
};

const applyTemplateFilters = (items, filters = {}) => {
  const search = (filters.search || '').trim().toLowerCase();

  return items.filter((item) => {
    const matchesSearch =
      !search ||
      item.title?.toLowerCase().includes(search) ||
      item.description?.toLowerCase().includes(search) ||
      item.tags?.some((tag) => tag.toLowerCase().includes(search));

    const matchesSection = !filters.sectionId || item.sectionId === filters.sectionId;
    const matchesCategory = !filters.categoryId || item.categoryId === filters.categoryId;

    return matchesSearch && matchesSection && matchesCategory;
  });
};

const findDashboardById = (id) => dashboardsStore.find((item) => item.id === id) || null;
const buildDashboardId = () => `dash-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const defaultDashboardDataAdapters = {
  async fetchDashboards(filters = {}) {
    await wait();
    return clone(applyFilters(dashboardsStore, filters));
  },
  async fetchTemplates(filters = {}) {
    await wait();
    return clone(applyTemplateFilters(templates, filters));
  },
  async fetchSections() {
    await wait();
    return clone(sections);
  },
  async fetchSectionFilters() {
    await wait();
    return flattenSectionsForFilter(clone(sections));
  },
  async fetchTemplateCategories() {
    await wait();
    return clone(templateCategories);
  },
  async getDashboardById(id) {
    await wait();
    const dashboard = findDashboardById(id);
    if (!dashboard) throw new Error('Dashboard not found');
    return clone(dashboard);
  },
  async getTemplateById(id) {
    await wait();
    const template = templates.find((item) => item.id === id);
    if (!template) throw new Error('Template not found');
    return clone({
      ...template,
      widgets: [],
      requirements: { requiredWidgetDefinitions: [], requiredCapabilities: [], minAppVersion: null },
    });
  },
  async createDashboard(payload = {}) {
    await wait();
    const widgets = Array.isArray(payload.widgets) ? payload.widgets : [];
    const sharedWith = Array.isArray(payload.sharedWith) ? payload.sharedWith : [];
    const nextDashboard = {
      id: buildDashboardId(),
      title: payload.title || 'Untitled Dashboard',
      description: payload.description || '',
      state: payload.state || DASHBOARD_STATES.DRAFT,
      sectionId: payload.sectionId || null,
      sectionPath: payload.sectionPath || '',
      creatorName: payload.creatorName || 'AIMS Team',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      widgetCount: widgets.length,
      widgets,
      tags: Array.isArray(payload.tags) ? payload.tags : [],
      sharedWith,
      hasDuplicateTitleSuffix: Boolean(payload.hasDuplicateTitleSuffix),
    };
    dashboardsStore = [...dashboardsStore, nextDashboard];
    return clone(nextDashboard);
  },
  /** @param {string} id */
  async duplicateDashboard(id) {
    await wait();
    const dashboard = findDashboardById(id);
    if (!dashboard) throw new Error('Dashboard not found');

    const baseTitle = getDuplicateBaseTitle(dashboard);

    /** Seed/mock dashboards use titleKey for display; the copy must use literal `title` only. */
    const dashboardRest = { ...dashboard };
    delete dashboardRest.titleKey;
    delete dashboardRest.descriptionKey;

    const duplicatedId = buildDashboardId();
    const widgets = Array.isArray(dashboard.widgets)
      ? dashboard.widgets.map((widget, index) => {
          const instanceId = `${duplicatedId}_${widget?.widgetType || 'widget'}_${index}`;
          return {
            ...widget,
            instanceId,
            layout: {
              ...(widget?.layout || {}),
              i: instanceId,
            },
          };
        })
      : [];
    const duplicated = {
      ...dashboardRest,
      id: duplicatedId,
      title: baseTitle,
      hasDuplicateTitleSuffix: true,
      state: DASHBOARD_STATES.DRAFT,
      archivedAt: null,
      archivedFromState: null,
      sharedWith: [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      widgetCount: widgets.length,
      widgets,
    };

    dashboardsStore = [...dashboardsStore, duplicated];
    return clone(duplicated);
  },
  async updateDashboard(id, payload = {}) {
    await wait();
    const index = dashboardsStore.findIndex((item) => item.id === id);
    if (index < 0) throw new Error('Dashboard not found');
    const current = dashboardsStore[index];
    const widgets = Array.isArray(payload.widgets) ? payload.widgets : current.widgets || [];
    const next = {
      ...current,
      ...payload,
      widgets,
      sharedWith: Array.isArray(payload.sharedWith) ? payload.sharedWith : current.sharedWith || [],
      widgetCount: widgets.length,
      updatedAt: new Date().toISOString(),
    };
    dashboardsStore = dashboardsStore.map((item, idx) => (idx === index ? next : item));
    return clone(next);
  },
  async publishDashboard(id) {
    await wait();
    const index = dashboardsStore.findIndex((item) => item.id === id);
    if (index < 0) throw new Error('Dashboard not found');
    const next = {
      ...dashboardsStore[index],
      state: DASHBOARD_STATES.PUBLISHED,
      updatedAt: new Date().toISOString(),
    };
    dashboardsStore = dashboardsStore.map((item, idx) => (idx === index ? next : item));
    return clone(next);
  },
  async getAvailableWidgets() {
    await wait();
    return clone(widgetLibrary);
  },
  async getShareCandidates() {
    await wait();
    return clone(shareCandidates);
  },
  async shareDashboard(id, assignments = []) {
    await wait();
    const index = dashboardsStore.findIndex((item) => item.id === id);
    if (index < 0) throw new Error('Dashboard not found');
    const currentShared = Array.isArray(dashboardsStore[index].sharedWith)
      ? dashboardsStore[index].sharedWith
      : [];
    const next = {
      ...dashboardsStore[index],
      sharedWith: [...currentShared, ...(Array.isArray(assignments) ? assignments : [])],
      updatedAt: new Date().toISOString(),
    };
    dashboardsStore = dashboardsStore.map((item, idx) => (idx === index ? next : item));
    return clone(next);
  },
};

export default defaultDashboardDataAdapters;
